import { useState } from 'react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Plus, Trash2, Copy, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_CREDENTIAL_TYPES, SENSITIVE_CRED_TYPES } from '../../../utils/bankConstants';

const CredentialsTab = ({ bank, canEdit, credentialInputs, setCredentialInputs, customCredentialTypes, editingField, editingValue, setEditingValue, startEditing, confirmEdit, cancelEdit, addCredentialType, deleteCredentialType, user }) => {
  const credentialTypes = [...DEFAULT_CREDENTIAL_TYPES, ...customCredentialTypes];
  const [showAddCred, setShowAddCred] = useState(false);
  const [newCredForm, setNewCredForm] = useState({ label: 'User ID', customLabel: '', value: '' });
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  const handleCopy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied!`); }
    catch { toast.error('Failed to copy'); }
  };

  const handleRemoveCredential = (index) => setCredentialInputs(credentialInputs.filter((_, i) => i !== index));

  const handleSubmitNewCred = () => {
    const label = newCredForm.label === 'Custom' ? newCredForm.customLabel : newCredForm.label;
    if (!label || !newCredForm.value) { toast.error('Both label and value are required'); return; }
    setCredentialInputs([...credentialInputs, { label: newCredForm.label, customLabel: newCredForm.customLabel, value: newCredForm.value }]);
    setNewCredForm({ label: 'User ID', customLabel: '', value: '' });
    setShowAddCred(false);
  };

  const handleAddNewType = async () => {
    const name = newTypeName.trim();
    if (!name) { toast.error('Type name is required'); return; }
    try {
      await addCredentialType(name);
      toast.success(`"${name}" added to credential types`);
      setNewTypeName('');
      setShowAddType(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add type');
    }
  };

  const handleDeleteType = async (name) => {
    try {
      await deleteCredentialType(name);
      toast.success(`"${name}" removed from credential types`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete type');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-2 divide-y">
        <div className="flex items-center justify-between px-3 py-2">
          <div>
            <h3 className="font-semibold text-sm text-indigo-800">Login Credentials</h3>
            <p className="text-xs text-slate-500 mt-0.5">{credentialInputs.length} credential(s) stored</p>
          </div>
          {canEdit && (
            <Button onClick={() => setShowAddCred(true)} variant="outline" size="sm" className="gap-1 border-indigo-300 text-indigo-700" data-testid="detail-add-credential-btn">
              <Plus className="w-3 h-3" /> Add
            </Button>
          )}
        </div>

        {credentialInputs.map((cred, idx) => {
          const label = cred.label === 'Custom' ? (cred.customLabel || cred.label) : cred.label;
          const sensitive = SENSITIVE_CRED_TYPES.includes(cred.label);
          const isEditing = editingField === `cred-${idx}`;

          return (
            <div key={`${cred.label}-${idx}`} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50 group" data-testid={`detail-credential-row-${idx}`}>
              <div className="flex-1 min-w-0">
                <span className="text-xs text-slate-500 block">{label}</span>
                {isEditing ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input data-testid={`edit-input-cred-${idx}`} value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') confirmEdit(`cred-${idx}`); if (e.key === 'Escape') cancelEdit(); }}
                      className="h-8 text-sm" autoFocus />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" onClick={() => confirmEdit(`cred-${idx}`)}><Check className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={cancelEdit}><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <span className="text-sm font-medium text-slate-800 block truncate">
                    {sensitive ? '••••••••' : (cred.value || '-')}
                  </span>
                )}
              </div>
              {!isEditing && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                  {cred.value && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`copy-cred-${idx}`} onClick={() => handleCopy(cred.value, label)}><Copy className="w-3 h-3 text-slate-400" /></Button>}
                  {canEdit && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-cred-${idx}`} onClick={() => startEditing(`cred-${idx}`, cred.value)}><Pencil className="w-3 h-3 text-blue-500" /></Button>}
                  {canEdit && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`detail-cred-delete-${idx}`} onClick={() => handleRemoveCredential(idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                </div>
              )}
            </div>
          );
        })}

        {credentialInputs.length === 0 && !showAddCred && (
          <div className="py-6 text-center text-sm text-slate-500">No credentials added yet</div>
        )}
      </Card>

      {showAddCred && (
        <Card className="p-4 space-y-3 border-indigo-200">
          <h4 className="text-sm font-semibold text-indigo-700">New Credential</h4>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="w-48">
              <Label className="text-xs">Type</Label>
              <Select value={newCredForm.label} onValueChange={(val) => setNewCredForm(p => ({ ...p, label: val }))}>
                <SelectTrigger className="h-9" data-testid="new-cred-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {credentialTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  <SelectItem value="Custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newCredForm.label === 'Custom' && (
              <div className="w-36">
                <Label className="text-xs">Custom Label</Label>
                <Input className="h-9" value={newCredForm.customLabel} onChange={(e) => setNewCredForm(p => ({ ...p, customLabel: e.target.value }))} placeholder="e.g. OTP Key" data-testid="new-cred-custom-label" />
              </div>
            )}
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs">Value</Label>
              <Input className="h-9" value={newCredForm.value} onChange={(e) => setNewCredForm(p => ({ ...p, value: e.target.value }))} placeholder="Enter value" data-testid="new-cred-value" />
            </div>
            <Button size="sm" onClick={handleSubmitNewCred} data-testid="submit-new-cred">Add</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowAddCred(false); setNewCredForm({ label: 'User ID', customLabel: '', value: '' }); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {user?.role === 'Admin' && (
        <Card className="p-4 border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-700">Manage Credential Types</h4>
            {!showAddType && (
              <Button onClick={() => setShowAddType(true)} variant="outline" size="sm" className="gap-1 text-xs" data-testid="manage-add-type-btn">
                <Plus className="w-3 h-3" /> New Type
              </Button>
            )}
          </div>
          {showAddType && (
            <div className="flex gap-2 items-center mb-3">
              <Input className="h-8 text-sm flex-1" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddNewType(); if (e.key === 'Escape') { setShowAddType(false); setNewTypeName(''); } }}
                placeholder="Enter new type name..." autoFocus data-testid="new-type-input" />
              <Button size="sm" className="h-8" onClick={handleAddNewType} data-testid="confirm-add-type">Add</Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => { setShowAddType(false); setNewTypeName(''); }}>Cancel</Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {DEFAULT_CREDENTIAL_TYPES.map(t => (
              <Badge key={t} variant="outline" className="text-xs text-slate-600">{t}</Badge>
            ))}
            {customCredentialTypes.map(t => (
              <Badge key={t} className="text-xs bg-indigo-100 text-indigo-800 gap-1 pr-1" data-testid={`custom-type-${t}`}>
                {t}
                <button onClick={() => handleDeleteType(t)} className="ml-1 hover:bg-indigo-200 rounded-full p-0.5" data-testid={`delete-type-${t}`}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">Default types (grey) cannot be removed. Custom types (blue) can be deleted.</p>
        </Card>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-xs text-amber-700">Click <strong>"Save Changes"</strong> at the top to persist credential updates.</p>
      </div>
    </div>
  );
};

export default CredentialsTab;
